/**
 * Modal para crear/editar una reunión
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import { X, Loader2, Calendar, Clock } from "lucide-react";
import { useCreateMeeting, useUpdateMeeting } from "@/hooks/useAttendance";
import { toJsDate } from "@/lib/services/activitySubmissions";
import { Timestamp } from "firebase/firestore";
import { todayLocalDateString } from "@/lib/utils";
import type { Meeting, MeetingType } from "@/types";

const meetingSchema = z.object({
  title: z.string().min(3, "El título debe tener al menos 3 caracteres"),
  type: z.string().min(1, "Selecciona un tipo de reunión"),
  date: z.string().min(1, "La fecha es requerida"),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  location: z.string().optional(),
  description: z.string().optional(),
});

type MeetingFormData = z.infer<typeof meetingSchema>;

interface MeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  periodId: string;
  meeting?: Meeting | null;
}

const meetingTypes: Array<{ value: MeetingType; label: string }> = [
  { value: "regular", label: "📅 Reunión Regular" },
  { value: "special", label: "⭐ Reunión Especial" },
  { value: "training", label: "📚 Capacitación" },
  { value: "service", label: "🤝 Servicio Comunitario" },
  { value: "other", label: "📌 Otro" },
];

export default function MeetingModal({
  isOpen,
  onClose,
  clubId,
  periodId,
  meeting,
}: MeetingModalProps) {
  const createMutation = useCreateMeeting();
  const updateMutation = useUpdateMeeting();
  const isEditing = !!meeting;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<MeetingFormData>({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      title: "",
      type: "regular",
      date: todayLocalDateString(),
      startTime: "14:00",
      endTime: "17:00",
      location: "",
      description: "",
    },
  });

  useEffect(() => {
    if (meeting) {
      const mDate = toJsDate(meeting.date);
      reset({
        title: meeting.title,
        type: meeting.type,
        date: (mDate ?? new Date()).toISOString().split("T")[0],
        startTime: meeting.startTime || "",
        endTime: meeting.endTime || "",
        location: meeting.location || "",
        description: meeting.description || "",
      });
    } else {
      reset({
        title: "",
        type: "regular",
        date: todayLocalDateString(),
        startTime: "14:00",
        endTime: "17:00",
        location: "",
        description: "",
      });
    }
  }, [meeting, reset]);

  const onSubmit = async (data: MeetingFormData) => {
    try {
      const meetingData = {
        title: data.title,
        type: data.type as MeetingType,
        date: Timestamp.fromDate(new Date(data.date + "T12:00:00")),
        startTime: data.startTime || undefined,
        endTime: data.endTime || undefined,
        location: data.location || undefined,
        description: data.description || undefined,
        clubId,
        periodId,
        isClosed: false,
        attendanceCount: 0,
        totalMembers: 0,
      };

      if (isEditing && meeting) {
        await updateMutation.mutateAsync({
          id: meeting.id,
          data: meetingData,
          clubId,
        });
      } else {
        await createMutation.mutateAsync(meetingData);
      }

      onClose();
    } catch (error) {
      console.error("Error al guardar reunión:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? "Editar Reunión" : "Nueva Reunión"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <Input
              {...register("title")}
              placeholder="Ej: Reunión semanal, Ensayo de marcha..."
              error={errors.title?.message}
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Reunión *
            </label>
            <select
              {...register("type")}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
            >
              {meetingTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>

          {/* Fecha */}
          <div>
            <DatePickerField
              control={control}
              name="date"
              label="Fecha *"
              error={errors.date?.message}
            />
          </div>

          {/* Horario */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock className="w-4 h-4 inline mr-1" />
                Hora Inicio
              </label>
              <Input {...register("startTime")} type="time" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora Fin
              </label>
              <Input {...register("endTime")} type="time" />
            </div>
          </div>

          {/* Ubicación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ubicación
            </label>
            <Input
              {...register("location")}
              placeholder="Ej: Salón de la iglesia, Campo de prácticas..."
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
              placeholder="Notas adicionales sobre la reunión..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? "Guardando..." : "Creando..."}
                </>
              ) : isEditing ? (
                "Guardar Cambios"
              ) : (
                "Crear Reunión"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
